import { isBrowserAdvertisingMeasurementGranted } from '@/lib/consent/advertising-consent'

type FbqWindow = Window & {
  fbq?: (...args: unknown[]) => void
}

/**
 * Fire a Meta (Facebook) Pixel standard event.
 *
 * No-ops when the pixel never loaded. The install snippet is env-gated on
 * `NEXT_PUBLIC_META_PIXEL_ID` (Production only — Preview is intentionally blank
 * so preview traffic stays out of ad reporting and audiences), so on Preview,
 * local dev, and during SSR `window.fbq` is simply undefined. Callers don't need
 * to branch on whether the pixel is configured.
 *
 * `eventID` is Meta's deduplication key. Nothing dedupes today — every event is
 * browser-side — but sending a stable ID now means that when the Conversions API
 * lands, the server fire can carry the same ID and Meta will collapse the pair
 * instead of double-counting the conversion.
 *
 * NEVER pass customer PII (name, email, phone, address). Advanced Matching
 * requires SHA-256 hashing and a separate privacy review; raw values must not
 * reach fbq.
 */
export function trackMetaPixel(
  event: string,
  params: Record<string, unknown> = {},
  options: { eventID?: string } = {},
): void {
  if (!isBrowserAdvertisingMeasurementGranted()) return
  const fbq = (window as FbqWindow).fbq
  if (typeof fbq !== 'function') return
  // Only pass the 4th argument when there's an ID to send — fbq treats an
  // options object with an undefined eventID as a malformed dedup key.
  if (options.eventID) {
    fbq('track', event, params, { eventID: options.eventID })
    return
  }
  fbq('track', event, params)
}

/**
 * A booking / callback form was submitted successfully.
 *
 * `Lead` is the standard event Meta's lead-generation campaign objective
 * optimises toward, so this is the fire that lets delivery chase completed
 * bookings instead of cheap landing-page clicks. The booking reference code
 * doubles as the dedup key — it is an opaque internal ID, not PII.
 */
export function trackMetaLead(input: {
  referenceCode: string
  intent: string
  pageType?: string
}): void {
  trackMetaPixel(
    'Lead',
    {
      content_name: input.intent,
      ...(input.pageType ? { content_category: input.pageType } : {}),
    },
    { eventID: input.referenceCode },
  )
}

/**
 * The customer opened a direct contact channel — tap-to-call or email.
 *
 * Meta's `Contact` standard event covers phone/SMS/email/chat. On a
 * high-intent local services site a tap-to-call is often a better conversion
 * signal than a form submit, so this is worth optimising toward alongside
 * `Lead`. No dedup key: a click is not a server-observable event, so there is
 * never a Conversions API counterpart to collapse it against.
 */
export function trackMetaContact(input: {
  channel: 'phone' | 'email'
  location: string
}): void {
  trackMetaPixel('Contact', {
    content_name: input.channel,
    content_category: input.location,
  })
}
